import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  CompletedActionInfoData
} from '../../../components/presentation/completed-action-info/completed-action-info.component';
import {
  InitialDataStateService
} from '../../../../core/services/impl/section-services/initial-data/initial-data-state.service';
import {SectionBase} from '../../../components/section/section-base';
import {NextOtherProductTheme} from '../../../components/next/next-other-products/next-other-products.component';
import {env, isKIOSKPOLIS, isNext} from '../../../../core/utils/utils';
import {StepKey} from '../../../../core/enums/step-key.enum';
import {NavigateManagerService} from '../../../../core/services/navigate-manager.service';
import {InitialDataSectionConfig} from './initial-data-section.config';
import {InitialDataSectionFieldKey} from '../../../../core/consts/initial-data-section-field-key.enum';
import {
  PredefinedValuesStateService
} from '../../../../core/services/impl/section-services/predefined-values/predefined-values-state.service';
import {WizardService} from '../../../../core/services/wizard.service';
import {Subscription} from 'rxjs/Rx';
import {TranslateService} from '@ngx-translate/core';
import {FieldsVisibilityService} from '../../../../core/services/fields-visibility.service';
import {DraftOfferApiService} from '../../../../core/services/impl/draft-offer-api.service';

@Component({
  selector: 'app-initial-data-section',
  templateUrl: './initial-data-section.component.html'
})
export class InitialDataSectionComponent extends SectionBase implements OnInit, OnDestroy {

  OtherProductsTheme = NextOtherProductTheme;
  SectionFieldKey = InitialDataSectionFieldKey;
  dataPresentationFields: string[];
  subscriptions: Subscription[] = [];
  registrationNumberVisible: boolean;
  birthDateVisible: boolean;

  aztecScannedCompletedActionInfoData: CompletedActionInfoData = {
    title: 'INITIAL_DATA_SECTION.AZTEC_SCANNED_COMPLETED_ACTION_INFO.title',
    buttonLabel: 'INITIAL_DATA_SECTION.AZTEC_SCANNED_COMPLETED_ACTION_INFO.buttonLabel'
  };

  customerAgreementPhotoCompletedActionInfoData: CompletedActionInfoData = {
    title: 'INITIAL_DATA_SECTION.CUSTOMER_AGREEMENT_COMPLETED_ACTION_INFO.title',
    buttonLabel: 'INITIAL_DATA_SECTION.CUSTOMER_AGREEMENT_COMPLETED_ACTION_INFO.buttonLabel'
  };

  private readonly SECTION_TITLE_FOR_NEXT_STEPS: string = 'INITIAL_DATA_SECTION.titleForNextSteps';

  get sectionsBellow(): boolean {
    return isKIOSKPOLIS() && this.navigateManagerService.currentStep.stepKey !== StepKey.FIRST_STEP_0;
  }

  get sectionVisible(): boolean {
    return this.initialDataStateService.sectionVisible && (this.registrationNumberVisible || this.birthDateVisible);
  }

  get unknownRegistrationNumberVisible(): boolean {
    return !this.previewMode && !isNext();
  }

  get shouldDisableRegistrationNumber(): boolean {
    return false;
    // return this.appStateService.state.unknownRegistrationNumber;
    // return this.appStateService.salesChannel === SalesChannel.YANOSIK && this.appStateService.state.calculationDataFromSession;
  }

  get unknownRegistrationNumberHintVisible(): boolean {
    return this.unknownRegistrationNumberVisible && this.initialDataStateService.unknownRegistrationNumber;
  }

  constructor(public initialDataStateService: InitialDataStateService,
              private predefinedState: PredefinedValuesStateService,
              private navigateManagerService: NavigateManagerService,
              private wizardService: WizardService,
              private ts: TranslateService,
              private fieldsVisibilityService: FieldsVisibilityService,
              private draftofferApiService: DraftOfferApiService) {
    super();
  }

  ngOnInit() {
    this.setFieldsVisibility();
    this.initBean();
    this.createConfig();
    this.buildForm();
    this.initControlsChanges();
    this.initFieldsState();
    this.prepareFieldsInPreviewMode();
    this.handleNextStepsTitle();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }

  createConfig(): void {
    const configBuilder = new InitialDataSectionConfig(this.initialDataStateService);
    this.formConfig = configBuilder.createFormConfig();
  }

  initBean(): void {
    this.bean = {
      ...this.initialDataStateService.vehicleData,
      ...this.initialDataStateService.ownerData,
    };
  }

  initControlsChanges(): void {
    this.handleControlsState(
        {
          controlName: InitialDataSectionFieldKey.REGISTRATION_NUMBER,
          behaviour: value => this.initialDataStateService.changeRegistrationNumber(value)
        },
        {
          controlName: InitialDataSectionFieldKey.UNKNOWN_REGISTRATION_NUMBER,
          behaviour: value => {
            this.initialDataStateService.handleUnknownRegistrationNumberChange(value);
            this.setRegistrationNumberState();
          }
        },
        {
          controlName: InitialDataSectionFieldKey.BIRTH_DATE,
          behaviour: value => {
            this.initialDataStateService.changeBirthDate(value);
            if (this.draftofferApiService.postDraftOfferAllowed) {
              this.draftofferApiService.postDraftOffer();
            }
          }
        }
    );
  }

  scanCustomerAgreement(): void {
    this.submitSection();
    if (this.isSectionValid()) {
      this.initialDataStateService.customerAgreementScanningMode = true;
    }
  }

  markAsDirty(event) {
    this.predefinedState.isDirtyBean = event;
  }

  registrationNumberClick(): void {
    if (this.getControl(InitialDataSectionFieldKey.UNKNOWN_REGISTRATION_NUMBER).value) {
      this.getControl(InitialDataSectionFieldKey.UNKNOWN_REGISTRATION_NUMBER).setValue(false);
    }
  }

  private handleNextStepsTitle(): void {
    if (this.wizardService.currentStepKey !== StepKey.FIRST_STEP_0) {
      this.updateTitleForNextSteps();
    }
    this.subscriptions.push(
    this.wizardService.stepChange$.subscribe(stepKey => {
      if (stepKey !== StepKey.FIRST_STEP_0) {
        this.updateTitleForNextSteps();
      }})
    );
  }

  private updateTitleForNextSteps(): void {
    this.ts.get(this.SECTION_TITLE_FOR_NEXT_STEPS).subscribe(title => this.title = title);
  }

  private initFieldsState() {
    // disable registration number field for YANOSIK
    if (this.shouldDisableRegistrationNumber) {
      this.disableField(InitialDataSectionFieldKey.REGISTRATION_NUMBER);
    }

    if (this.initialDataStateService.isRegistrationNumberUnknown) {
      this.initialDataStateService.changeUnknownRegistrationNumber(true);
    }
    this.setRegistrationNumberState();
  }

  private setRegistrationNumberState(): void {
    if (this.initialDataStateService.unknownRegistrationNumber) {
      this.getControl(InitialDataSectionFieldKey.REGISTRATION_NUMBER).reset(null, {emitEvent: false});
      this.getControl(InitialDataSectionFieldKey.REGISTRATION_NUMBER).clearValidators();
    } else {
      this.getFieldConfig(InitialDataSectionFieldKey.REGISTRATION_NUMBER).validators = this.initialDataStateService.registrationNumberValidators;
      this.initControlsValidators(InitialDataSectionFieldKey.REGISTRATION_NUMBER);
    }
    this.getControl(InitialDataSectionFieldKey.REGISTRATION_NUMBER).updateValueAndValidity();
  }

  private prepareFieldsInPreviewMode(): void {
    this.dataPresentationFields = [
      InitialDataSectionFieldKey.REGISTRATION_NUMBER,
      InitialDataSectionFieldKey.BIRTH_DATE,
    ];
  }

  private setFieldsVisibility(): void {
    this.registrationNumberVisible = this.fieldsVisibilityService.isFieldVisible('INITIAL_DATA_SECTION', InitialDataSectionFieldKey.REGISTRATION_NUMBER);
    this.birthDateVisible = this.fieldsVisibilityService.isFieldVisible('INITIAL_DATA_SECTION', InitialDataSectionFieldKey.BIRTH_DATE);
  }

}
